import FirstPageIcon from "@mui/icons-material/FirstPage";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Tab, Tabs, Typography } from "@mui/material";
import Gutters from "Layouts/Responsive/Gutters";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import { dateTimeColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import { AcxTabPanel } from "components/UI/AcxTabPanel";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import AutoRequeueStore from "./AutoRequeueStore";
import AcxMainTextField from "components/UI/AcxMainTextField";

const requeuedAmdColumns: IColDef[] = [
    {
        headerName: "Retry Id",
        field: "id",
        type: "string",
        flex: 1,
    },
    {
        headerName: "AudioMetadata Id",
        field: "audioMetadataId",
        type: "string",
        flex: 1,
    },
    {
        headerName: "Last Retried On",
        field: "lastRetriedOn",
        flex: 1,
        ...dateTimeColumnType,
    },
    {
        headerName: "Status",
        field: "status",
        type: "string",
        flex: 1,
    },
    {
        headerName: "Retry Count",
        field: "retryCount",
        type: "number",
        flex: 1,
    },
];

const processingAmdColumns: IColDef[] = [
    {
        headerName: "AudioMetadata Id",
        field: "id",
        type: "string",
        flex: 1,
    },
    {
        headerName: "Arrived On",
        field: "arrivedOn",
        flex: 1,
        ...dateTimeColumnType,
    },
];

const AutoRequeue = observer(() => {
    const store = useStore(AutoRequeueStore);
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        store.loadAll().catch(() => {
            messageStore.logError(
                "Unable to fetch auto-requeue data. Please retry.",
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStore.orgStore.selectedOrganization?.id]);

    const makeErrHandler = (msg: string) => () =>
        messageStore.logError(msg);

    const requeuedControls = [
        {
            controlElement: (
                <AcxMainTextField
                    id="min-hours-ago"
                    labelText="Min Hours Ago"
                    type="number"
                    value={store.minHoursAgoRetried}
                    inputProps={{ min: 1 }}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1) {
                            store.setMinHoursAgoRetried(val);
                        }
                            
                    }}
                    containerStyle={{ width: "140px" }}
                />
            ),
            style: {},
        },
        {
            controlElement: (
                <AcxMainTextField
                    id="min-retry-count"
                    labelText="Min Retry Count"
                    type="number"
                    value={store.minRetryCount}
                    inputProps={{ min: 1 }}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1) {
                            store.setMinRetryCount(val);
                        }
                    }}
                    containerStyle={{ width: "140px" }}
                />
            ),
            style: {},
        },
        {
            controlElement: (
                <AcxMainTextField
                    id="take"
                    labelText="Take"
                    type="number"
                    value={store.take}
                    inputProps={{ min: 1, max: 100 }}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1 && val <= 100) {
                            store.setTake(val);
                        }
                    }}
                    containerStyle={{ width: "100px" }}
                />
            ),
            style: {},
        },
        <AcxButton
            tooltip="Go to first page"
            color="primary"
            onClick={() =>
                store.loadRequeuedAmds("first").catch(makeErrHandler("Unable to fetch retried AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.requeuedAmdsDgStore.isLoading}
            disabled={store.requeuedAmdsDgStore.isLoading || !store.requeuedAmdsHasPrev}
        >
            <FirstPageIcon />
        </AcxButton>,
        <AcxButton
            tooltip="Previous page"
            color="primary"
            onClick={() =>
                store.loadRequeuedAmds("prev").catch(makeErrHandler("Unable to fetch retried AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.requeuedAmdsDgStore.isLoading}
            disabled={store.requeuedAmdsDgStore.isLoading || !store.requeuedAmdsHasPrev}
        >
            <NavigateBeforeIcon />
        </AcxButton>,
        {
            controlElement: (
                <Typography variant="body2" style={{ alignSelf: "center", whiteSpace: "nowrap" }}>
                    Page {store.requeuedAmdsPage}
                </Typography>
            ),
            style: {},
        },
        <AcxButton
            tooltip="Next page"
            color="primary"
            onClick={() =>
                store.loadRequeuedAmds("next").catch(makeErrHandler("Unable to fetch retried AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.requeuedAmdsDgStore.isLoading}
            disabled={store.requeuedAmdsDgStore.isLoading || !store.requeuedAmdsHasMore}
        >
            <NavigateNextIcon />
        </AcxButton>,
        <AcxButton
            tooltip="Refresh — reload first page"
            color="primary"
            onClick={() =>
                store.loadRequeuedAmds("first").catch(makeErrHandler("Unable to fetch retried AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.requeuedAmdsDgStore.isLoading}
            disabled={store.requeuedAmdsDgStore.isLoading}
        >
            <RefreshIcon />
        </AcxButton>,
    ];

    const processingControls = [
        {
            controlElement: (
                <AcxMainTextField
                    id="min-hours-ago"
                    labelText="Min Hours Ago"
                    type="number"
                    value={store.minHoursAgoProcessing}
                    inputProps={{ min: 1 }}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1) {
                            store.setMinHoursAgoProcessing(val);
                        }
                    }}
                    containerStyle={{ width: "140px" }}
                />
            ),
            style: {},
        },
        {
            controlElement: (
                <AcxMainTextField
                    id="take"
                    labelText="Take"
                    type="number"
                    value={store.take}
                    inputProps={{ min: 1, max: 100 }}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 1 && val <= 100) {
                            store.setTake(val);
                        }
                    }}
                    containerStyle={{ width: "100px" }}
                />
            ),
            style: {},
        },
        <AcxButton
            tooltip="Go to first page"
            color="primary"
            onClick={() =>
                store.loadProcessingAmds("first").catch(makeErrHandler("Unable to fetch processing AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.processingAmdsDgStore.isLoading}
            disabled={store.processingAmdsDgStore.isLoading || !store.processingAmdsHasPrev}
        >
            <FirstPageIcon />
        </AcxButton>,
        <AcxButton
            tooltip="Previous page"
            color="primary"
            onClick={() =>
                store.loadProcessingAmds("prev").catch(makeErrHandler("Unable to fetch processing AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.processingAmdsDgStore.isLoading}
            disabled={store.processingAmdsDgStore.isLoading || !store.processingAmdsHasPrev}
        >
            <NavigateBeforeIcon />
        </AcxButton>,
        {
            controlElement: (
                <Typography variant="body2" style={{ alignSelf: "center", whiteSpace: "nowrap" }}>
                    Page {store.processingAmdsPage}
                </Typography>
            ),
            style: {},
        },
        <AcxButton
            tooltip="Next page"
            color="primary"
            onClick={() =>
                store.loadProcessingAmds("next").catch(makeErrHandler("Unable to fetch processing AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.processingAmdsDgStore.isLoading}
            disabled={store.processingAmdsDgStore.isLoading || !store.processingAmdsHasMore}
        >
            <NavigateNextIcon />
        </AcxButton>,
        <AcxButton
            tooltip="Refresh — reload first page"
            color="primary"
            onClick={() =>
                store.loadProcessingAmds("first").catch(makeErrHandler("Unable to fetch processing AMDs. Please retry."))
            }
            style={{ height: "32px" }}
            loading={store.processingAmdsDgStore.isLoading}
            disabled={store.processingAmdsDgStore.isLoading}
        >
            <RefreshIcon />
        </AcxButton>,
    ];

    useEffect(() => {
        store.requeuedAmdsDgStore.setColumns(requeuedAmdColumns);
        store.requeuedAmdsDgStore.controls = requeuedControls;
        store.processingAmdsDgStore.setColumns(processingAmdColumns);
        store.processingAmdsDgStore.controls = processingControls;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    });

    return (
        <Gutters>
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                style={{ marginBottom: "8px" }}
            >
                <Tab label={`Retried AMDs (${store.requeuedAmds.length})`} />
                <Tab
                    label={`Stuck in Processing (${store.processingAmds.length})`}
                />
            </Tabs>
            <AcxTabPanel
                value={activeTab}
                index={0}
                style={{ height: "calc(100% - 56px)" }}
            >
                <AcxDataGrid dataGridStore={store.requeuedAmdsDgStore} />
            </AcxTabPanel>
            <AcxTabPanel
                value={activeTab}
                index={1}
                style={{ height: "calc(100% - 56px)" }}
            >
                <AcxDataGrid dataGridStore={store.processingAmdsDgStore} />
            </AcxTabPanel>
        </Gutters>
    );
});

export default AutoRequeue;
